import { useRef, useEffect, useState } from 'react';
import {ReactComponent as GlobeIcon} from '../../assets/images/Icons/globe.svg'
import {ReactComponent as BluekaliHorizontalLogo} from '../../assets/images/Logos/BluekaliHorizontalLogo.svg'
import Hamburger from './Hamburger';
import SharkyEatingNoodles from '../../assets/images/Sharky/Sharky_EatingNoodles.png'
import { useTranslation } from 'react-i18next';

import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollSmoother } from 'gsap/ScrollSmoother'
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollSmoother)

const sections = ['navigation:about', 'navigation:impact', 'navigation:wear', 'navigation:solutions', 'navigation:contact'];
const sectionIds = ['section-about', 'section-impact', 'section-wear', 'section-solutions', 'section-contact'];

export default function Navbar({ onSelect }) {
  const { t, i18n } = useTranslation(['common', 'navigation']);
  const [activeIndex, setActiveIndex] = useState(0);
  const [underlineStyle, setUnderlineStyle] = useState({});
  const navRefs = useRef([]);
  const navContainerRef = useRef(null);
  const [isBlurred, setIsBlurred] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Función para actualizar la posición de la línea
  const updateUnderlinePosition = (index) => {
    const activeButton = navRefs.current[index];
    const navContainer = navContainerRef.current;
    if (activeButton && navContainer) {
      const containerRect = navContainer.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const left = buttonRect.left - containerRect.left;
      const width = buttonRect.width;
      setUnderlineStyle({
        left: `${left}px`,
        width: `${width}px`,
        opacity: 1
      });
    }
  };

  useEffect(() => {
  if (isMenuOpen) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }

  // cleanup en caso de que el componente se desmonte con el menú abierto
  return () => {
    document.body.classList.remove('overflow-hidden');
  };
}, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      const targetOffset = ScrollSmoother.get()?.offset('.section-about');
      if (targetOffset) {
        // const targetPosition = targetElement.offsetTop;
        setIsBlurred(scrollY >= targetOffset - 10);
      }else{
        const targetElement = document.getElementById('section-about');
        const targetPosition = targetElement.offsetTop;
        setIsBlurred(scrollY >= targetPosition - 10);
      }
      
      // const darkElement = document.getElementById('section-wear');
      const darkOffset = ScrollSmoother.get()?.offset('.section-wear')
      if (darkOffset) {
        // const darkPosition = darkElement.offsetTop;
        const isMobile = (window.innerWidth < 768);
        setDarkMode(scrollY >= darkOffset - 35 && scrollY <= darkOffset-35 + window.innerHeight && !isMobile);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Actualizar posición inicial y cuando cambie el índice activo
  useEffect(() => {
    updateUnderlinePosition(activeIndex);
  }, [activeIndex, i18n.language]);

  // Actualizar posición cuando la ventana cambie de tamaño
  useEffect(() => {
    const handleResize = () => {
      updateUnderlinePosition(activeIndex);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeIndex]);

  const handleButtonClick = (index) => {
    setActiveIndex(index);
    onSelect?.(index);
    setIsMenuOpen(false);

    const scrollTarget = document.getElementById(sectionIds[index]);
    if (scrollTarget) {
      // console.log("scrolling to " + sectionIds[index] + " at " + scrollTarget.offsetTop)
      
      let pos ="";
      const isMobileSolutions = (window.innerWidth < 1024);
      if(index === 3 && !isMobileSolutions) {
        pos = "bottom bottom"
      }else {
        pos = "top top"
      }
      ScrollSmoother.get()?.scrollTo(scrollTarget, true, pos)

      if (!ScrollSmoother.get()) {
        // console.log("scroll movil")
        scrollTarget.scrollIntoView()
      }
    }else{
      console.log("Can't scroll: didn't find id: " + sectionIds[index]);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({top:0});
  }

  const toggleMenu = () => {    
    setIsMenuOpen(!isMenuOpen);
  }

  useEffect(() => {
    setLangMenuOpen(false);
    const handleResize = () => {
      if (isMenuOpen && window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  useGSAP(() => {
    let mm = gsap.matchMedia();

    gsap.delayedCall(0.3, () => {
      mm.add("(min-width: 768px)", () => {
      sectionIds.forEach((id, index) => {
        ScrollTrigger.create({
          trigger: `#${id}`,
          start: "top center",
          end: "bottom center",
          onEnter: () => setActiveIndex(index),
          onEnterBack: () => setActiveIndex(index),
        })
      })
    });
    });
    
  
  })

  return (
    <div className={`fixed top-0 flex justify-between items-start h-28 z-[100] w-dvw pt-4 ${
      isBlurred ? 'navbar-blur' : 'bg-transparent'
    }`}>
      
      {/* Pseudo-elemento con blur y máscara */}
      {isBlurred && (
        <div 
          className="absolute w-full h-full top-0 transition-opacity duration-300"
          style={{
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            WebkitMask: 'linear-gradient(0deg, transparent, black 50%)',
            mask: 'linear-gradient(0deg, transparent, black 50%)',
            zIndex: -1
          }}
          
        />
      )}

      <BluekaliHorizontalLogo className={`md:hover:cursor-pointer h-8 ml-6 transition-all duration-300 transform translate-y-[3px] ${
        darkMode ? 'text-bluekali-black' : 'text-bluekali-white'
      }`} onClick={() => scrollToTop()}/>
      <nav
        ref={navContainerRef}
        className="hidden md:flex md:flex-nowrap justify-center md:gap-4 lg:gap-12 p-1 relative"
      >
        {sections.map((title, index) => (
          <button
            key={title}
            ref={(el) => (navRefs.current[index] = el)}
            onClick={() => handleButtonClick(index)}
            className={`select-none relative px-4 py-2 text-xs font-gotham font-medium transition-all duration-300 ${
              activeIndex === index ? 'text-opacity-100' : 'text-opacity-70 hover:text-opacity-90'
            } ${darkMode ? 'text-bluekali-black' : 'text-bluekali-white'}`}
          >
            {t(title)}
          </button>
        ))}
        <span
          className={`absolute bottom-0 h-[2px] transition-all duration-200 ease-out rounded-full ${
            darkMode ? 'bg-bluekali-black' : 'bg-bluekali-white'
          }`}
          style={underlineStyle}
        />
      </nav>

      <button className='hidden md:block mr-8 transform translate-y-[9px]'
      onClick={() => setLangMenuOpen(!langMenuOpen)}
      >
        <GlobeIcon className={`size-5 transition-all duration-300 ${
        darkMode ? 'text-bluekali-black' : 'text-bluekali-white'
      }`}/>
      </button>
      <button className='block md:hidden z-[70]' onClick={() => toggleMenu()}>
        <Hamburger darkMode={darkMode}/>
      </button>

      {/* menu for mobile */}
      <div className={`fixed top-0 left-0 w-full h-screen md:hidden bg-bluekali-black/60 transition-opacity duration-300 ${
        isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`} onClick={() => setIsMenuOpen(false)}>
        {/* Menu items container */}
        <div className="flex flex-col items-center justify-center h-full relative">
          
          {/* Menu items with staggered animation */}
          <div className="flex flex-col h-full w-full items-end space-y-2 text-white pr-6 pt-20">
            <p 
               
              className={`text-xl font-medium transform transition-all duration-200 ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              onClick={() => {handleButtonClick(0)}}
              style={{ transitionDelay: isMenuOpen ? '100ms' : '0ms' }}
            >
              {t("navigation:about")}
            </p>
            
            <p 
              className={`text-xl font-medium transform transition-all duration-200 ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              onClick={() => {handleButtonClick(1)}}
              style={{ transitionDelay: isMenuOpen ? '200ms' : '0ms' }}
            >
              {t("navigation:impact")}
            </p>
            
            <p
              className={`text-xl font-medium transform transition-all duration-200 ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              onClick={() => {handleButtonClick(2)}}
              style={{ transitionDelay: isMenuOpen ? '300ms' : '0ms' }}
            >
              {t("navigation:wear")}
            </p>
            
            <p
              className={`text-xl font-medium transform transition-all duration-200 ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              onClick={() => {handleButtonClick(3)}}
              style={{ transitionDelay: isMenuOpen ? '400ms' : '0ms' }}
            >
              {t("navigation:solutions")}
            </p>
            
            <p 
              href="#contact" 
              className={`text-xl font-medium transform transition-all duration-200 ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              onClick={() => {handleButtonClick(4)}}
              style={{ transitionDelay: isMenuOpen ? '500ms' : '0ms' }}
            >
              {t("navigation:contact")}
            </p>

            <button 
              className={`text-xl font-medium transform transition-all duration-200 cursor-pointer ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: isMenuOpen ? '600ms' : '0ms' }}
              onClick={(event) => {
              event.stopPropagation();
              // Aquí va tu lógica
              setLangMenuOpen(!langMenuOpen)
              console.log("opening languages")
            }}>
              <GlobeIcon className="h-6 w-auto mt-8" />
            </button>

            { langMenuOpen && window.innerWidth < 768 &&
            <div className=' mr-10 mt-4 p-2 px-6 min-w-40 bg-bluekali-white rounded-s-lg rounded-b-lg text-bluekali-cerulean-blue font-bold flex flex-col'>
              <button
              onClick={() => i18n.changeLanguage("es")}
              className={`select-none ${i18n.language === "es" ? "font-bold" : "font-normal text-sm"}`}>
                Spanish
              </button>
              <button
              onClick={() => i18n.changeLanguage("en")}
              className={`select-none ${i18n.language === "en" ? "font-bold" : "font-normal text-sm"}`}>
                English
              </button>
            </div>
            }

          </div>
          
          {/* Animated image from bottom left */}
          <div className="absolute bottom-8 left-8">
            <img 
              src={SharkyEatingNoodles}
              alt="Menu decoration" 
              className={`w-auto h-80 transform translate-y-1/3 transition-all duration-700 -rotate-12 ease-out ${
                isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-80 opacity-0'
              }`}
              style={{ transitionDelay: isMenuOpen ? '600ms' : '0ms' }}
            />
          </div>
        </div>
      </div>

      { langMenuOpen && window.innerWidth >= 768 &&
      <div className='absolute bottom-0 right-4 mr-10 mt-4 p-2 px-6 min-w-40 bg-bluekali-white rounded-s-lg rounded-b-lg text-bluekali-cerulean-blue font-bold flex flex-col'>
        <button
        onClick={() => i18n.changeLanguage("es")}
        className={`select-none ${i18n.language === "es" ? "font-bold" : "font-normal text-sm"}`}>
          Spanish
        </button>
        <button
        onClick={() => i18n.changeLanguage("en")}
        className={`select-none ${i18n.language === "en" ? "font-bold" : "font-normal text-sm"}`}>
          English
        </button>
      </div>
      }
    </div>
  );
}